import { useCallback, useEffect, useState } from 'react'
import { Plus, Trash2, AlertTriangle } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, Skeleton } from '@/components/States'
import { ApiError } from '@/lib/api'
import {
  fetchRules,
  createRule,
  toggleRule,
  deleteRule,
  fetchBreaches,
  PRIORITIES,
  ES_STR as S,
  type EscalationRule,
  type Breach,
} from './escalationApi'

const prioColor: Record<string, string> = {
  urgent: 'text-danger', high: 'text-warning', normal: 'text-secondary', low: 'text-tertiary',
}

export function EscalationScreen() {
  const { toast } = useToast()
  const [rules, setRules] = useState<EscalationRule[] | null>(null)
  const [breaches, setBreaches] = useState<Breach[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  const [name, setName] = useState('')
  const [hours, setHours] = useState(24)
  const [prio, setPrio] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchRules(ac.signal).then(setRules).catch((e) => {
      if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
    })
    fetchBreaches(ac.signal).then(setBreaches).catch(() => setBreaches([]))
    return () => ac.abort()
  }, [tick])

  const create = async () => {
    if (!name.trim()) return
    setBusy(true)
    try {
      await createRule({ name: name.trim(), unanswered_hours: hours, priority_filter: prio })
      setName('')
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }
  const toggle = async (r: EscalationRule) => {
    setRules((prev) => prev?.map((x) => (x.id === r.id ? { ...x, enabled: !x.enabled } : x)) ?? prev)
    try {
      await toggleRule(r.id, !r.enabled)
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
      reload()
    }
  }
  const del = async (id: number) => {
    try {
      await deleteRule(id)
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }

  return (
    <>
      <PageHeader title={S.title} screenId="A7.17" description={S.desc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-3xl space-y-4">
          {/* Create rule */}
          <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border-default bg-surface-2 p-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={S.name} className="min-w-[140px] flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent" />
            <label className="text-xs text-tertiary">
              {S.hours}
              <input type="number" min={1} value={hours} onChange={(e) => setHours(Number(e.target.value) || 24)} className="mt-1 block w-24 rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary" />
            </label>
            <label className="text-xs text-tertiary">
              {S.priority}
              <select value={prio} onChange={(e) => setPrio(e.target.value)} className="mt-1 block rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary">
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>{p || S.anyPriority}</option>
                ))}
              </select>
            </label>
            <button type="button" onClick={create} disabled={busy || !name.trim()} className="flex items-center gap-1 rounded-md bg-accent px-3 py-2 text-sm text-white disabled:opacity-50">
              <Plus size={14} />
              {S.add}
            </button>
          </div>

          {err && <ErrorState code={err.code} onRetry={reload} />}

          {/* Rules */}
          {!rules ? (
            <Skeleton className="h-10 w-full" />
          ) : rules.length === 0 ? (
            <p className="py-3 text-center text-sm text-tertiary">{S.noRules}</p>
          ) : (
            <div className="overflow-hidden rounded-lg border border-border-default">
              {rules.map((r, i) => (
                <div key={r.id} className={`flex items-center gap-3 px-4 py-2.5 ${i > 0 ? 'border-t border-border-subtle' : ''}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-primary">{r.name}</p>
                    <p className="text-xs text-tertiary">&gt; {r.unanswered_hours}h · {r.priority_filter || S.anyPriority}</p>
                  </div>
                  <button type="button" role="switch" aria-checked={r.enabled} onClick={() => toggle(r)} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${r.enabled ? 'bg-accent' : 'bg-surface-3'}`}>
                    <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${r.enabled ? 'translate-x-5' : 'translate-x-1'}`} />
                  </button>
                  <button type="button" onClick={() => del(r.id)} className="rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-danger" title={S.delete}>
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Breaches */}
          <div>
            <p className="mb-2 flex items-center gap-1.5 text-xs font-medium text-secondary">
              <AlertTriangle size={13} className="text-warning" />
              {S.breaches}
            </p>
            {!breaches ? (
              <Skeleton className="h-10 w-full" />
            ) : breaches.length === 0 ? (
              <p className="rounded-lg border border-border-default bg-surface-2 px-4 py-3 text-sm text-tertiary">{S.noBreaches}</p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-warning/40">
                {breaches.map((b, i) => (
                  <div key={`${b.ticket_id}-${i}`} className={`flex items-center gap-3 px-4 py-2 text-sm ${i > 0 ? 'border-t border-border-subtle' : ''}`}>
                    <span className="font-mono text-xs text-tertiary">#{b.ticket_id}</span>
                    <span className="min-w-0 flex-1 truncate text-primary">{b.subject || '—'}</span>
                    <span className={`text-xs ${prioColor[b.priority] ?? ''}`}>{b.priority}</span>
                    <span className="shrink-0 text-xs text-warning tabular">{b.age_hours.toFixed(0)}h</span>
                    <span className="shrink-0 text-xs text-tertiary">{b.rule}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
